import dayjs, { Dayjs } from "../vendor/DayJs";

import * as Pillars from "../Pillars";
import * as HardwarePillars from "../HardwarePillars";
import * as Flags from "../Flags";
import { YEAR_VIEW_ENABLED } from "../../Config";
import { TimeUnit } from "../consts/DatetimeConstants";
import { HW_APP_TYPE, HwEventType } from "lib/consts/HardwareConstants";
import { isFiveKeysTheme } from "./ThemeUtils";

export type PillarType = "speed" | "distraction" | "fatigue" | "risk" | "eco";

export interface PillarChartDataPoint {
	date: number;
	level: string;
	total: number | null;
	engineEvents: number;
	businessMileage: number;
	liveUsers: number;
}

export type PillarChartData = [Array<string>, ...Array<[string, ...number[]]>];

export type PillarChartDataCollection = Record<string, PillarChartData>;

export const MONTH_LABEL_FORMAT_SHORT = "MMM";
export const WEEK_LABEL_FORMAT_SHORT = "D MMM";

export const MONTH_LABEL_FORMAT_LONG = "MMMM";
export const WEEK_LABEL_FORMAT_LONG = "D MMMM";

export const getLabel = (
	timestamp: number,
	timeSelection: TimeSelection,
	long = false,
): string => {
	let key = "";

	const debugFormat = Flags.DEBUG_FEATURES.is() ? "Z" : "";

	const start = dayjs(timestamp).utc().startOf(timeSelection.grouping.unit);

	if (timeSelection.grouping.unit === "month") {
		key = start.format(
			(long ? MONTH_LABEL_FORMAT_LONG : MONTH_LABEL_FORMAT_SHORT) + debugFormat,
		);
	} else if (timeSelection.grouping.unit === "week") {
		key = start.format(
			(long ? WEEK_LABEL_FORMAT_LONG : WEEK_LABEL_FORMAT_SHORT) + debugFormat,
		);
	}

	return key;
};

// Time range stuff

export interface TimeGrouping {
	unit: TimeUnit;
	blocks: number;
}

export interface TimeSpan {
	from: number;
	to: number;
}

export type TimeSelectionId =
	| "4 weeks"
	| "2 months"
	| "3 months"
	| "6 months"
	| "12 months";

export interface TimeSelection {
	displayName: string;
	id: TimeSelectionId;
	grouping: TimeGrouping;
	formatLong: string;
	formatShort: string;
}

export const TIME_RANGE_14_DAYS = {
	grouping: { unit: TimeUnit.WEEK, blocks: 2 },
};

export const TIME_SELECTION: Record<string, TimeSelection> = {
	WEEKS_4: {
		displayName: "%controls_time_select_last_4_weeks",
		id: "4 weeks",
		grouping: { unit: TimeUnit.WEEK, blocks: 4 },
		formatLong: WEEK_LABEL_FORMAT_LONG,
		formatShort: WEEK_LABEL_FORMAT_SHORT,
	},
	MONTHS_2: {
		displayName: "%controls_time_select_last_2_months",
		id: "2 months",
		grouping: { unit: TimeUnit.WEEK, blocks: 8 },
		formatLong: WEEK_LABEL_FORMAT_LONG,
		formatShort: WEEK_LABEL_FORMAT_SHORT,
	},
	MONTHS_3: {
		displayName: "%controls_time_select_last_3_months",
		id: "3 months",
		grouping: { unit: TimeUnit.MONTH, blocks: 3 },
		formatLong: MONTH_LABEL_FORMAT_LONG,
		formatShort: MONTH_LABEL_FORMAT_SHORT,
	},
	MONTHS_6: {
		displayName: "%controls_time_select_last_6_months",
		id: "6 months",
		grouping: { unit: TimeUnit.MONTH, blocks: 6 },
		formatLong: MONTH_LABEL_FORMAT_LONG,
		formatShort: MONTH_LABEL_FORMAT_SHORT,
	},
};

if (YEAR_VIEW_ENABLED) {
	TIME_SELECTION.MONTHS_12 = {
		displayName: "%controls_time_select_last_12_months",
		id: "12 months",
		grouping: { unit: TimeUnit.MONTH, blocks: 12 },
		formatLong: MONTH_LABEL_FORMAT_LONG,
		formatShort: MONTH_LABEL_FORMAT_SHORT,
	};
}

export const DEFAULT_TIME_SELECTION = TIME_SELECTION.MONTHS_2;

export const getTimeSelectionById = (
	timeSelectionId: string,
): TimeSelection => {
	const timeSelection = Object.values(TIME_SELECTION).find(
		(range) => range.id === timeSelectionId,
	);
	if (!timeSelection) {
		return TIME_SELECTION.MONTHS_2;
		// throw new Error("Invalid time range: " + timeSelectionId);
	}
	return timeSelection;
};

export const getTimeSpan = (
	timeSelectionOrId: Pick<TimeSelection, "grouping"> | string,
): TimeSpan => {
	let timeRange = timeSelectionOrId as TimeSelection;

	if (typeof timeSelectionOrId === "string") {
		timeRange = getTimeSelectionById(timeSelectionOrId);
	}

	const to = dayjs()
		.utc()
		.subtract(1, timeRange.grouping.unit)
		.endOf(timeRange.grouping.unit)
		.valueOf();
	const from = dayjs()
		.utc()
		.subtract(timeRange.grouping.blocks, timeRange.grouping.unit)
		.startOf(timeRange.grouping.unit)
		.valueOf();

	return { from, to };
};

export const getTimeSelectionBlocks = (timeSelectionId: string): number[] => {
	const { blocks, unit } = getTimeSelectionById(timeSelectionId).grouping;
	let { from, to } = getTimeSpan(timeSelectionId);
	to = dayjs(to).utc().startOf(unit).valueOf();

	if (blocks === 2) {
		return [from, to];
	}

	const remainingBlocks = Array.from(new Array(blocks - 2)).map((_, index) => {
		return dayjs(from)
			.utc()
			.add(index + 1, unit)
			.startOf(unit)
			.valueOf();
	});

	return [from, to, ...remainingBlocks].sort((a, b) => a - b);
};

export const formatChartData = (
	data: { [key: string]: any[] },
	pillarId: string,
	timeSelectionId: string,
	minMileage?: number,
	appType?: string,
) => {
	let appTypePillars: any = Pillars.PILLARS_WITH_SCORE;

	if (appType === HW_APP_TYPE) {
		appTypePillars = [Pillars.DRIVING_SCORE, ...HardwarePillars.PILLARS];
	} else if (isFiveKeysTheme()) {
		appTypePillars = Pillars.FIVE_KEYS_PILLARS_WITH_SCORE;
	}

	const pillar = (appTypePillars as any).find(
		(pillar: any) => pillar.id === pillarId,
	);

	if (!pillar) {
		return;
	}

	const timeSelectionBlocks = getTimeSelectionBlocks(timeSelectionId);

	return Object.keys(data).reduce((journeys: any, key: string) => {
		const levelData = data[key as keyof typeof data] || [];
		let validJourneys: any[] = [];

		timeSelectionBlocks.forEach((timeSelectionBlock) => {
			const journeyBlockData = levelData.find(
				(journey: any) => journey.timestamp === timeSelectionBlock,
			);

			if (
				!journeyBlockData ||
				journeyBlockData.businessMileage < (minMileage || 10_000)
			) {
				validJourneys.push({
					timestamp: timeSelectionBlock,
					businessTrips: journeyBlockData?.businessTrips || 0,
					businessMileage: journeyBlockData?.businessMileage || 0,
					personalTrips: 0,
					personalMileage: 0,
					numberOfDrivers: journeyBlockData?.numberOfDrivers || 0,
					lastWeeksDrivingScore: null,
					lastWeeksBusinessMileage: null,
					noData:
						!journeyBlockData ||
						journeyBlockData.businessMileage < 1 ||
						journeyBlockData.noData,
					lowMileage:
						journeyBlockData?.businessMileage &&
						journeyBlockData.businessMileage < 10_000,
					pillars: {
						[pillar.data.key]: 0,
					},
				});
			} else {
				validJourneys.push(journeyBlockData);
			}
		});

		validJourneys = validJourneys.sort((a, b) => a.timestamp - b.timestamp);

		validJourneys.forEach((block: any) => {
			if (!timeSelectionBlocks.includes(block.timestamp)) {
				return;
			}

			const timeSelection = getTimeSelectionById(timeSelectionId);
			const date = dayjs(block.timestamp);
			const name = date
				.utc()
				.format(
					timeSelection.grouping.unit === TimeUnit.WEEK ? "D MMM" : "MMM",
				);
			const fullName = date
				.utc()
				.format(
					timeSelection.grouping.unit === TimeUnit.WEEK ? "D MMMM" : "MMMM",
				);

			let score;

			if (appType === HW_APP_TYPE) {
				let hwScorePillar = block.pillars.hardwarePillars?.find(
					(hwPillar: any) => {
						return hwPillar.type === pillarId;
					},
				);

				if (pillarId === Pillars.DRIVING_SCORE.id) {
					hwScorePillar = block.pillars.drivingScore;
				}

				score = hwScorePillar?.score ?? block.pillars[pillar.data.key];
			} else {
				score =
					block.pillars[pillar.data.key]?.score ??
					block.pillars[pillar.data.key];

				if (score === undefined) {
					const splitKey = pillar.data.key.split(".");

					if (splitKey.length > 1) {
						let pillarObject = block.pillars;

						splitKey.forEach((keyPart: string) => {
							if (pillarObject) {
								pillarObject = pillarObject[keyPart];
							}
						});

						score = pillarObject?.score ?? pillarObject;
					}
				}
			}

			if (!journeys[name]) {
				journeys[name] = {
					name,
					fullName,
					[key]: {
						...block,
						total: typeof score === "number" ? Number(score?.toFixed(2)) : null,
					},
				};
			} else {
				journeys[name][key] = {
					...block,
					total: typeof score === "number" ? Number(score?.toFixed(2)) : null,
				};
			}
		});

		return journeys;
	}, {});
};

export const getHwPillarUnit = (eventType: HwEventType) => {
	let unit = "";

	switch (eventType) {
		case HwEventType.EVENTS:
			unit = " Events";
			break;
		case HwEventType.PERCENTAGE:
			unit = "%";
			break;
		case HwEventType.DURATION:
			// TODO: Once the duration is wired in convert the given metric to 1h 40m format
			unit = " Minutes";
			break;
		case HwEventType.SCORE:
			unit = "";
			break;
		default:
			unit = "";
	}

	return unit;
};
